/**
 * Micro-graphiques de tableau.
 *
 * Un tableau de trente-deux cotes doit répondre à « où est le problème ? » sans un clic.
 * Deux visuels y suffisent, et aucun n'est décoratif :
 *
 * - la **sparkline** montre la forme de la série — une dérive, un saut, un procédé
 *   stable — ce qu'aucun indice résumé ne dit ;
 * - la **barre de capabilité** situe la dispersion dans la tolérance, ce qui explique
 *   *pourquoi* le Cpk vaut ce qu'il vaut : décentrage ou dispersion excessive.
 *
 * SVG en ligne plutôt qu'ECharts : trente-deux instances d'une bibliothèque de graphiques
 * dans un tableau qui défile coûteraient plusieurs centaines de millisecondes à chaque
 * rendu, pour dessiner quarante pixels.
 */

import { useMemo } from "react";

import { cn, verdictOf } from "./primitives";

const STROKE: Record<string, string> = {
  good: "stroke-good",
  warn: "stroke-warn",
  bad: "stroke-bad",
  unknown: "stroke-faint",
};

export function Sparkline({
  points,
  cpk,
  violations = [],
  width = 96,
  height = 24,
  className,
}: {
  points: number[];
  cpk?: number | null;
  violations?: number[];
  width?: number;
  height?: number;
  className?: string;
}) {
  const geometry = useMemo(() => {
    if (points.length < 2) return null;

    // Sous-échantillonnage : au-delà d'une centaine de points sur cent pixels, on
    // dessine plusieurs traits par pixel — du coût sans information supplémentaire.
    const step = Math.max(1, Math.ceil(points.length / 100));
    const sampled = points.filter((_, i) => i % step === 0);

    const min = Math.min(...sampled);
    const max = Math.max(...sampled);
    const span = max - min || 1;
    const dx = width / (sampled.length - 1 || 1);

    const y = (v: number) => height - 2 - ((v - min) / span) * (height - 4);
    const path = sampled
      .map(
        (v, i) =>
          `${i === 0 ? "M" : "L"}${(i * dx).toFixed(1)},${y(v).toFixed(1)}`,
      )
      .join(" ");

    // `sampled[i]` peut être absent si le sous-échantillonnage a écarté ce point ;
    // TypeScript le sait avec `noUncheckedIndexedAccess`, et il a raison.
    const flagged = violations
      .map((i) => Math.floor(i / step))
      .filter((i) => i >= 0 && i < sampled.length && sampled[i] !== undefined)
      .slice(0, 12)
      .map((i) => ({ x: i * dx, y: y(sampled[i] as number) }));

    return { path, flagged };
  }, [points, violations, width, height]);

  if (!geometry) {
    return <span className="text-[0.75rem] text-faint">—</span>;
  }

  const tone = STROKE[verdictOf(cpk)];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn("overflow-visible", className)}
      role="img"
      aria-label={`Évolution sur ${points.length} points`}
    >
      <path
        d={geometry.path}
        fill="none"
        strokeWidth={1.25}
        className={cn(tone, "opacity-80")}
      />
      {geometry.flagged.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={1.8} className="fill-bad" />
      ))}
    </svg>
  );
}

/**
 * Position de la dispersion dans la tolérance.
 *
 * La barre représente l'intervalle de tolérance ; le bloc coloré, l'étendue à ±3σ des
 * mesures. Un bloc étroit et centré est un procédé sain ; un bloc décalé vers une limite
 * dit « décentrage » ; un bloc plus large que la barre dit « dispersion ». C'est le
 * diagnostic que le Cpk résume en un nombre, rendu visible.
 */
export function CapabilityBar({
  mean,
  sigma,
  lsl,
  usl,
  cpk,
  className,
}: {
  mean: number;
  sigma: number;
  lsl: number | null;
  usl: number | null;
  cpk?: number | null;
  className?: string;
}) {
  if (lsl === null || usl === null || usl <= lsl || !Number.isFinite(sigma)) {
    return (
      <span className="text-[0.75rem] text-faint">tolérance inconnue</span>
    );
  }

  const span = usl - lsl;
  // La vue déborde de 20 % de part et d'autre : une dispersion qui sort de la tolérance
  // doit se voir sortir, pas être tronquée au bord — c'est précisément le cas grave.
  const from = lsl - span * 0.2;
  const to = usl + span * 0.2;
  const pct = (v: number) => ((v - from) / (to - from)) * 100;

  const low = Math.max(0, pct(mean - 3 * sigma));
  const high = Math.min(100, pct(mean + 3 * sigma));
  const tone = verdictOf(cpk);
  const fill =
    tone === "good" ? "bg-good" : tone === "warn" ? "bg-warn" : "bg-bad";

  return (
    <div
      className={cn("relative h-4 w-full min-w-24", className)}
      title="Dispersion ±3σ dans la tolérance"
    >
      {/* Intervalle de tolérance */}
      <div
        className="absolute inset-y-1 rounded-sm border border-line-strong bg-surface-2"
        style={{ left: `${pct(lsl)}%`, right: `${100 - pct(usl)}%` }}
      />
      {/* Dispersion observée */}
      <div
        className={cn("absolute inset-y-1.5 rounded-xs opacity-70", fill)}
        style={{ left: `${low}%`, width: `${Math.max(high - low, 1)}%` }}
      />
      {/* Moyenne */}
      <div
        className="absolute inset-y-0 w-px bg-ink/60"
        style={{ left: `${Math.min(Math.max(pct(mean), 0), 100)}%` }}
      />
    </div>
  );
}
